/**
 * Product Detail Info Page - iPad-Optimized
 *
 * Alternative product detail view with left sidebar menu
 * Displays basic info, official documents, and product information
 *
 * Touch targets: All menu items and buttons ≥44x44px
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input, Typography, Empty, Space, Avatar, Spin, Tag, Row, Col, Descriptions } from "antd";
import dayjs from "dayjs";
import { SearchOutlined, FolderOpenOutlined, FileTextOutlined, CloseOutlined, FilePdfOutlined, EyeOutlined, DownloadOutlined, StarOutlined, FileImageOutlined, FileOutlined } from "@ant-design/icons";
import { getProductInfo, getProductCompanyList, getMetadataLabel, getSelectMetadataByCode, useCountryStore } from "@ifa/utils";
import type { ProductInfoVO, Material, ProductCompanyListVO } from "@ifa/utils";
import CustomButton from "../components/CustomButton";
import CustomCard from "../components/CustomCard";
import { FilePreviewModal } from "../components/FilePreviewModal";
import styles from "./OrderDetail/index.module.css";
import "./ProductDetailInfo.css";

const { Title, Text } = Typography;

// Sidebar Navigation Items
const sidebarItems = [
  { key: "basic_info", label: "基本資料", icon: <FileTextOutlined /> },
  { key: "highlights", label: "產品亮點", icon: <StarOutlined /> },
  { key: "product_info", label: "產品資訊", icon: <FolderOpenOutlined /> },
  { key: "official_docs", label: "官方檔案", icon: <FilePdfOutlined /> },
];

interface DocumentItem {
  id: string;
  name: string;
  size: string;
  date: string;
  url?: string;
}

export function ProductDetailInfo() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { allCountries, fetchCountryList } = useCountryStore();
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("basic_info");
  const [loading, setLoading] = useState(true);

  // Data States
  const [productInfo, setProductInfo] = useState<ProductInfoVO | null>(null);
  const [companyInfo, setCompanyInfo] = useState<ProductCompanyListVO | null>(null);
  const [documentsMap, setDocumentsMap] = useState<Record<string, DocumentItem[]>>({});

  // Preview Modal State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string }>();

  // Fetch country list on mount
  useEffect(() => {
    fetchCountryList();
  }, []);

  useEffect(() => {
    if (productId) {
      fetchData(productId);
    }
  }, [productId]);

  const fetchData = async (id: string) => {
    setLoading(true);
    try {
      // 1. Get Product Info
      const pInfo: ProductInfoVO = await getProductInfo(id);
      setProductInfo(pInfo);

      // 2. Get Company Info
      if (pInfo.companyCode) {
        const companies = await getProductCompanyList();
        const company = companies.find((c) => c.code === pInfo.companyCode);
        if (company) {
          setCompanyInfo(company);
        }
      }

      // 3. Process Documents
      const newDocsMap: Record<string, DocumentItem[]> = {};

      // Helper to process files
      const processFiles = (files: Material[] = [], categoryKey: string) => {
        files.forEach((file, index) => {
          const doc: DocumentItem = {
            id: `${categoryKey}-${index}`,
            name: file.fileName || "未命名檔案",
            size: formatFileSize(file.fileSize),
            date: file.uploadTime ? dayjs(file.uploadTime).format("YYYY-MM-DD") : "-",
            url: file.filePath,
          };

          if (!newDocsMap[categoryKey]) {
            newDocsMap[categoryKey] = [];
          }
          newDocsMap[categoryKey].push(doc);
        });
      };

      // Process official files (files) as "official_docs"
      processFiles(pInfo.files, "official_docs");

      // Process custom files (customFiles) as "product_info"
      processFiles(pInfo.customFiles, "product_info");

      setDocumentsMap(newDocsMap);
    } catch (err) {
      console.error("Failed to fetch product data:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return "-";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleBack = () => {
    navigate("/products");
  };

  const getCategoryName = (key: string) => {
    const item = sidebarItems.find((c) => c.key === key);
    return item ? item.label : "資料列表";
  };

  // Helper function to get license plate label
  const getLicensePlateLabel = (code: string | undefined) => {
    if (!code) return "-";
    const metadata = getSelectMetadataByCode("LICENSE_PLATE");
    const item = metadata?.items?.find((i) => i.key === code);
    return item?.name || code;
  };

  // Helper function to get country name
  const getCountryName = (code?: string) => {
    if (!code) return "N/A";
    const country = allCountries.find((c) => c.countryCode === code);
    return country?.cname || country?.name || code;
  };

  // Helper function to get file type from filename
  const getFileType = (fileName: string): "pdf" | "image" | "other" => {
    if (!fileName) return "other";
    const ext = fileName.toLowerCase().split(".").pop() || "";
    if (ext === "pdf") return "pdf";
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext)) return "image";
    return "other";
  };

  // Handle file preview
  const handlePreview = (doc: DocumentItem) => {
    if (!doc.url) return;
    const fileType = getFileType(doc.name);
    if (fileType === "pdf" || fileType === "image") {
      setPreviewFile({ url: doc.url, name: doc.name });
      setPreviewOpen(true);
    } else {
      // For other file types, open in new tab
      window.open(doc.url, "_blank");
    }
  };

  // Handle file download
  const handleDownload = (doc: DocumentItem) => {
    if (!doc.url) return;
    window.open(doc.url, "_blank");
  };

  const renderDocumentList = (documents: DocumentItem[]) => {
    // Filter by search text
    const filteredDocuments = documents.filter((doc) => doc.name.toLowerCase().includes(searchText.toLowerCase()));

    if (filteredDocuments.length === 0) {
      return (
        <div className="empty-state">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暫無相關資料" />
        </div>
      );
    }

    return (
      <div className="document-list">
        {filteredDocuments.map((doc, index) => {
          const fileType = getFileType(doc.name);
          let icon = <FileOutlined className="doc-icon" />;
          let iconBgColor = "#e5e7eb";
          let iconColor = "#6b7280";

          if (fileType === "pdf") {
            icon = <FilePdfOutlined className="doc-icon" />;
            iconBgColor = "#fee2e2";
            iconColor = "#ef4444";
          } else if (fileType === "image") {
            icon = <FileImageOutlined className="doc-icon" />;
            iconBgColor = "#dbeafe";
            iconColor = "#3b82f6";
          }

          return (
            <div key={index} className="document-card">
              <div className="doc-icon-wrapper" style={{ backgroundColor: iconBgColor }}>
                <span style={{ color: iconColor, fontSize: 24 }}>{icon}</span>
              </div>
              <div className="doc-info">
                <div className="doc-name">{doc.name}</div>
                <div className="doc-meta">
                  {doc.size} <span className="meta-separator"></span> {doc.date}
                </div>
              </div>
              <div className="doc-actions">
                <CustomButton bgColor="transparent" icon={<EyeOutlined />} className="action-preview" onClick={() => handlePreview(doc)}>
                  預覽
                </CustomButton>
                <CustomButton bgColor="#00C7B6" icon={<DownloadOutlined />} className="action-download" onClick={() => handleDownload(doc)}>
                  下載
                </CustomButton>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="loading-container" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <Spin size="large" tip="載入中..." />
        </div>
      );
    }

    // 1. Basic Info (基本資料) - 包含產品描述、產品亮點、資料
    if (selectedCategory === "basic_info") {
      return (
        <div className="product-info-container" style={{ padding: "0 0 24px", overflowY: "auto", height: "100%" }}>
          {/* 1.1 產品描述 */}
          <div className="info-section" style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#333", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <FileTextOutlined style={{ color: "#00c7b6", fontSize: 18 }} />
              <span>產品描述</span>
            </div>
            <CustomCard style={{ marginBottom: 0 }}>
              {/* Company Info */}
              {companyInfo && (
                <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid #f0f0f0" }}>
                  <Space align="center" size="middle">
                    <Avatar size={56} shape="square" src={companyInfo.logo} style={{ backgroundColor: "#fff", border: "1px solid #f0f0f0" }}>
                      {!companyInfo.logo && companyInfo.name?.charAt(0)}
                    </Avatar>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: "#333", marginBottom: 4 }}>{companyInfo.name}</div>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {companyInfo.englishName}
                      </Text>
                    </div>
                  </Space>
                </div>
              )}

              {/* Product Basic Info */}
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      產品名稱
                    </Text>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>{productInfo?.productName || "-"}</div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      產品類型
                    </Text>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>{productInfo?.type || "-"}</div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      產品上市日期
                    </Text>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>{productInfo?.launchDate ? dayjs(productInfo.launchDate).format("YYYY-MM-DD") : "-"}</div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      最後更新時間
                    </Text>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>{productInfo?.updateTime ? dayjs(productInfo.updateTime).format("YYYY-MM-DD HH:mm") : "-"}</div>
                </Col>
                {productInfo?.region && (
                  <Col span={12}>
                    <div style={{ marginBottom: 4 }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        銷售區域
                      </Text>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>{getMetadataLabel("COUNTRY", productInfo.region)}</div>
                  </Col>
                )}
                {productInfo?.sellStatus && (
                  <Col span={12}>
                    <div style={{ marginBottom: 4 }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        銷售狀態
                      </Text>
                    </div>
                    <Tag color={productInfo.sellStatus === "SELLING" ? "green" : "default"}>{productInfo.sellStatus === "SELLING" ? "銷售中" : productInfo.sellStatus}</Tag>
                  </Col>
                )}
              </Row>

              {/* License Plate */}
              {productInfo?.licensePlate && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #f0f0f0" }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      牌照
                    </Text>
                  </div>
                  <Space wrap>
                    {(Array.isArray(productInfo.licensePlate) ? productInfo.licensePlate : [productInfo.licensePlate]).map((lp, idx) => (
                      <Tag key={idx} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", color: "#64748b" }}>
                        {getLicensePlateLabel(lp) || "-"}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {/* Product Description Text */}
              {productInfo?.desc && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #f0f0f0" }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      產品簡介
                    </Text>
                  </div>
                  <div style={{ color: "#334155", lineHeight: 1.8, fontSize: 14, whiteSpace: "pre-wrap" }}>{productInfo.desc}</div>
                </div>
              )}

              {/* Tags */}
              {productInfo?.tags && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #f0f0f0" }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      產品標籤
                    </Text>
                  </div>
                  <Space wrap>
                    {productInfo.tags.split(",").map((tag, idx) => (
                      <Tag key={idx} color="blue">
                        {tag.trim()}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}

              {/* Payment & Coverage Options */}
              {(productInfo?.frequencies || productInfo?.currencies || productInfo?.policyPeriods) && (
                <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #f0f0f0" }}>
                  <Row gutter={[16, 16]}>
                    {productInfo?.frequencies && productInfo.frequencies.length > 0 && (
                      <Col span={12}>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            繳費頻率
                          </Text>
                        </div>
                        <Space wrap>
                          {productInfo.frequencies.map((freq, idx) => (
                            <Tag key={idx}>{getMetadataLabel("FREQUENCY", freq) || freq}</Tag>
                          ))}
                        </Space>
                      </Col>
                    )}
                    {productInfo?.currencies && productInfo.currencies.length > 0 && (
                      <Col span={12}>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            可選貨幣
                          </Text>
                        </div>
                        <Space wrap>
                          {productInfo.currencies.map((curr, idx) => (
                            <Tag key={idx} color="cyan">
                              {getMetadataLabel("CURRENCY", curr) || curr}
                            </Tag>
                          ))}
                        </Space>
                      </Col>
                    )}
                    {productInfo?.policyPeriods && productInfo.policyPeriods.length > 0 && (
                      <Col span={24}>
                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 13 }}>
                            保障年限
                          </Text>
                        </div>
                        <Space wrap>
                          {productInfo.policyPeriods.map((period, idx) => (
                            <Tag key={idx} color="orange">
                              {getMetadataLabel("PERIOD", period) || period}
                            </Tag>
                          ))}
                        </Space>
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </CustomCard>
          </div>

          {/* 1.2 產品亮點 */}
          {productInfo?.highlights && productInfo.highlights.length > 0 && (
            <div className="info-section" style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "#333", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <StarOutlined style={{ color: "#00c7b6", fontSize: 18 }} />
                <span>產品亮點</span>
              </div>
              <CustomCard>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {productInfo.highlights.map((item, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 12 }} className="highlight-item">
                      <span style={{ fontSize: 20, lineHeight: 1, marginTop: 2, flexShrink: 0, color: "#00c7b6" }}>⭐️</span>
                      <span style={{ fontSize: 15, lineHeight: 1.8, color: "#333", fontWeight: 500 }}>{item.zhHantHK || item.zhHansCN || item.enUS || ""}</span>
                    </div>
                  ))}
                </div>
              </CustomCard>
            </div>
          )}

          {/* 1.3 資料 (Documents) */}
          {documentsMap["product_info"] && documentsMap["product_info"].length > 0 && (
            <div className="info-section">
              <div style={{ fontSize: 16, fontWeight: 600, color: "#333", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <FolderOpenOutlined style={{ color: "#1890ff", fontSize: 18 }} />
                <span>資料</span>
              </div>
              {renderDocumentList(documentsMap["product_info"])}
            </div>
          )}
        </div>
      );
    }

    // 2. Product Highlights (產品亮點)
    if (selectedCategory === "highlights") {
      if (!productInfo?.highlights || productInfo.highlights.length === 0) {
        return <Empty description="暫無產品亮點" />;
      }

      return (
        <div className="product-info-container" style={{ padding: "0 0 24px", overflowY: "auto", height: "100%" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {productInfo.highlights.map((item, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: 12 }} className="highlight-item">
                <span style={{ fontSize: 20, lineHeight: 1, marginTop: 2, flexShrink: 0, color: "#00c7b6" }}>⭐️</span>
                <span style={{ fontSize: 15, lineHeight: 1.8, color: "#333", fontWeight: 500 }}>{item.zhHantHK || item.zhHansCN || item.enUS || ""}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // 3. Product Info (產品資訊) - Display custom documents
    if (selectedCategory === "product_info") {
      const documents = documentsMap["product_info"] || [];
      return <div style={{ height: "100%", overflow: "auto" }}>{renderDocumentList(documents)}</div>;
    }

    // 4. Official Documents (官方文件) - Display official files
    if (selectedCategory === "official_docs") {
      const documents = documentsMap["files"] || [];
      return <div style={{ height: "100%", overflow: "auto" }}>{renderDocumentList(documents)}</div>;
    }

    return <Empty description="暫無資料" />;
  };

  return (
    <div className="product-detail-info-container">
      {/* Top Navigation Bar */}
      <div className="product-detail-info-navbar">
        <div className="navbar-left">
          <span className="nav-breadcrumb-link" onClick={handleBack}>
            產品
          </span>
          <RightOutlinedIcon />
          <span className="nav-product-name">{productInfo?.productName || "載入中..."}</span>
        </div>
        <div className="navbar-right">
          <CustomButton bgColor="transparent" icon={<CloseOutlined />} onClick={handleBack} className="close-button" />
        </div>
      </div>

      {/* Main Content */}
      <div className="product-detail-info-content">
        <div className="info-layout">
          {/* Left Sidebar */}
          <div className={styles["sidebar"]}>
            {sidebarItems.map((item) => (
              <div key={item.key} className={`${styles["sidebar-item"]} ${selectedCategory === item.key ? styles["active"] : ""}`} onClick={() => setSelectedCategory(item.key)}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Right Content Area */}
          <div className="info-main">
            <div className="content-header">
              <Title level={4} style={{ margin: 0 }}>
                {getCategoryName(selectedCategory)}
              </Title>
              {selectedCategory === "product_info" && (
                <Input
                  size="large"
                  placeholder="搜尋..."
                  prefix={<SearchOutlined />}
                  style={{ width: 240, borderRadius: 20 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  allowClear
                />
              )}
            </div>

            <div className="content-body" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* File Preview Modal */}
      <FilePreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} file={previewFile} />
    </div>
  );
}

// Simple Icon Component for Breadcrumb
function RightOutlinedIcon() {
  return (
    <svg
      viewBox="64 64 896 896"
      focusable="false"
      data-icon="right"
      width="1em"
      height="1em"
      fill="currentColor"
      aria-hidden="true"
      style={{ fontSize: 12, margin: "0 8px", color: "rgba(0,0,0,0.45)" }}
    >
      <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.4z"></path>
    </svg>
  );
}
